export class TextSubstitutionUtility {
    static BEGIN_DELIMITER = "[!";
    static END_DELIMITER = "!]";

    static notAllowedInFields = [
        " ",
        "!",
        "@",
        "#",
        "$",
        "%",
        "^",
        "&",
        "*",
        "(",
        ")",
        "_",
        "+",
        "-",
        "=",
        "{",
        "}",
        "[",
        "]",
        ":",
        ";",
        "'",
        "<",
        ">",
        "?",
        ",",
        ".",
        "/",
        '"',
    ];

    // returns field name if cursor is in a field but not on any other text
    static getSelectedField(text: string, beginIndex: number, endIndex?: number): string | undefined {
        let newIndex = this.correctSelection(text, beginIndex, endIndex ?? beginIndex);

        // its not a field
        if (beginIndex === endIndex && !newIndex) return undefined;

        let s = text.substring(newIndex?.newBegin ?? beginIndex, newIndex?.newEnd ?? endIndex);

        if (s.length === 0) return undefined;

        if (s.startsWith(this.BEGIN_DELIMITER) && s.endsWith(this.END_DELIMITER)) {
            // now check if there is just one field...

            let internalString = s.substring(2, s.length - 2);

            if (this.hasNotAllowed(internalString)) return undefined;

            return internalString;
        }

        return undefined;
    }

    static hasNotAllowed(text: string): boolean {
        for (let notAllowed of this.notAllowedInFields) {
            if (text.indexOf(notAllowed) !== -1) return true;
        }
        return false;
    }

    static correctSelection(
        text: string,
        beginIndex: number,
        endIndex: number,
        returnUndefinedIfNotChanged: boolean = false
    ): { newBegin: number; newEnd: number } | undefined {
        let newBegin: number | undefined = undefined;
        let newEnd: number | undefined = undefined;

        newEnd = endIndex;
        newBegin = beginIndex;

        let selection = text.substring(beginIndex, endIndex);

        if (beginIndex === 0) {
            // skip
        } else if (selection.startsWith(this.BEGIN_DELIMITER)) {
            // skip
        } else if (text.charAt(beginIndex) === "!" && text.charAt(beginIndex - 1) === "[") {
            newBegin = beginIndex - 1;
        } else {
            // work forward to see if we are in a field
            let stringBefore = text.substring(0, beginIndex);

            stringBefore = stringBefore.split("").reverse().join("");

            let adjustBegin: number = 0;
            let firstCharIsField: boolean = false;
            for (let c of stringBefore.split("")) {
                if (firstCharIsField && c === "[") {
                    newBegin = beginIndex - (adjustBegin + 1);
                    break;
                }
                if (c === "!") {
                    firstCharIsField = true;
                    adjustBegin = adjustBegin + 1;
                    continue;
                }

                firstCharIsField = false;
                if (this.hasNotAllowed(c)) {
                    break;
                }

                adjustBegin = adjustBegin + 1;
            }
        }

        // if it starts with begin delimiter ... begin does not need to be corrected
        if (endIndex === text.length) {
            // skip
        } else if (selection.endsWith(this.END_DELIMITER)) {
            // skip
        } else if (text.charAt(endIndex - 1) === "!" && text.charAt(endIndex) === "]") {
            newEnd = endIndex + 1;
        } else {
            // work forward to see if we are in a field
            let stringAfter = text.substring(endIndex);

            let adjustEnd: number = 0;
            let firstCharIsField: boolean = false;
            for (let c of stringAfter.split("")) {
                if (firstCharIsField && c === "]") {
                    newEnd = endIndex + adjustEnd + 1;
                    break;
                }
                if (c === "!") {
                    firstCharIsField = true;
                    adjustEnd = adjustEnd + 1;
                    continue;
                }

                firstCharIsField = false;
                if (this.hasNotAllowed(c)) {
                    break;
                }

                adjustEnd = adjustEnd + 1;
            }
        }

        // fix where the selection is not a valid field
        if (newBegin !== beginIndex) {
            let selectedText = text.substring(newBegin + 2, newEnd);

            let firstCharIsField: boolean = false;
            let isField: boolean = false;
            for (let c of selectedText.split("")) {
                if (firstCharIsField && c === "]") {
                    isField = true;
                    break;
                }
                if (c === "!") {
                    firstCharIsField = true;
                    continue;
                }

                if (this.hasNotAllowed(c)) {
                    break;
                }
            }

            if (!isField) newBegin = beginIndex;
        }

        if (newEnd !== endIndex) {
            let selectedText = text.substring(newBegin, newEnd - 2);

            let firstCharIsField: boolean = false;
            let isField: boolean = false;
            for (let c of selectedText.split("").reverse()) {
                if (firstCharIsField && c === "[") {
                    isField = true;
                    break;
                }
                if (c === "!") {
                    firstCharIsField = true;
                    continue;
                }

                if (this.hasNotAllowed(c)) {
                    break;
                }
            }

            if (!isField) newEnd = endIndex;
        }

        if (returnUndefinedIfNotChanged && newBegin === beginIndex && newEnd === endIndex) return undefined;

        return { newBegin: newBegin ?? beginIndex, newEnd: newEnd ?? endIndex };
    }

    static getFields(text: string): string[] {
        const regex = /\[!(.*?)!\]/g;
        const match = text.match(regex);

        if (match === null) return [];

        let fields = match.map((m) => m.substring(2, m.length - 2)).map((m) => m);

        return Array.from(new Set(fields));
    }

    static replace(text: string, data: { fieldName: string; fieldValue: string }[], defaultValue?: string): string {
        let fields = this.getFields(text);

        for (let f of fields) {
            text = text.replaceAll(
                this.BEGIN_DELIMITER + f + this.END_DELIMITER,
                data.find((d) => {
                    return d.fieldName === f;
                })?.fieldValue ??
                    defaultValue ??
                    ""
            );
        }

        return text;
    }
}
